import React, { FC, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { Alert, Button, Input, ModalBody } from 'reactstrap';

import ColumnVisibilityTable from './ColumnVisibilityTable';
import { OwnedColumn, VisibilityOption } from './types';

interface FileUploadStepProps {
  error: string;
  loadingColumns: boolean;
  ownedColumns: OwnedColumn[];
  savingVisibility: boolean;
  visibilityDirty: boolean;
  visibilitySaved: boolean;
  visibilityOptions: VisibilityOption[];
  fileInputRef: RefObject<HTMLInputElement>;
  deletingColumnKey: string | null;
  onOwnedColumnVisibilityChange: (key: string, visibility: string) => void;
  onOwnedColumnLabelChange: (key: string, label: string) => void;
  onDeleteOwnedColumn: (key: string) => void;
  onSaveVisibility: () => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const FileUploadStep: FC<FileUploadStepProps> = ({
  error,
  loadingColumns,
  ownedColumns,
  savingVisibility,
  visibilityDirty,
  visibilitySaved,
  visibilityOptions,
  fileInputRef,
  deletingColumnKey,
  onOwnedColumnVisibilityChange,
  onOwnedColumnLabelChange,
  onDeleteOwnedColumn,
  onSaveVisibility,
  onFileChange,
}) => (
  <ModalBody>
    {error && (
      <Alert color="danger" className="mb-3">
        {error}
      </Alert>
    )}
    {loadingColumns ? (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
      </div>
    ) : (
      <>
        {ownedColumns.length > 0 && (
          <>
            <h5>
              <FormattedMessage
                id="app.custom_campaign_data.file_upload.existing_columns_title"
                defaultMessage="Your custom columns"
              />
            </h5>
            <ColumnVisibilityTable
              columns={ownedColumns}
              visibilityOptions={visibilityOptions}
              onChange={onOwnedColumnVisibilityChange}
              onLabelChange={onOwnedColumnLabelChange}
              onDelete={onDeleteOwnedColumn}
              deletingColumnKey={deletingColumnKey}
            />
            <div className="d-flex align-items-center mb-4">
              <Button
                color="primary"
                size="sm"
                disabled={savingVisibility || !visibilityDirty}
                onClick={onSaveVisibility}
              >
                {savingVisibility ? (
                  <FormattedMessage
                    id="app.custom_campaign_data.file_upload.saving"
                    defaultMessage="Saving..."
                  />
                ) : (
                  <FormattedMessage
                    id="app.custom_campaign_data.file_upload.save_changes"
                    defaultMessage="Save changes"
                  />
                )}
              </Button>
              {visibilitySaved && (
                <span className="text-success ms-2">
                  <i className="fe fe-check me-1" />
                  <FormattedMessage
                    id="app.custom_campaign_data.file_upload.saved"
                    defaultMessage="Saved"
                  />
                </span>
              )}
            </div>
            <hr />
          </>
        )}
        <h5>
          <FormattedMessage
            id="app.custom_campaign_data.file_upload.upload_title"
            defaultMessage="Upload new data"
          />
        </h5>
        <p>
          <FormattedMessage
            id="app.custom_campaign_data.file_upload.description"
            defaultMessage="Upload a CSV file with custom data columns. The file must include an identifier column (email or external ID) to match people."
          />
        </p>
        <Input
          type="file"
          accept=".csv"
          innerRef={fileInputRef}
          onChange={onFileChange}
        />
      </>
    )}
  </ModalBody>
);

export default FileUploadStep;
